import React, { useEffect, useState } from 'react';
import { t } from 'i18next';

import './index.less';

import { ProjectWidgetOutput } from '../../../gamma-api';
import { PUBLIC_URL } from '../../utils/configUtil';
import { getDefaultIfUndefined } from '../../utils/typeUtil';
import { toNumberOrUndefined } from '../../utils/numberUtil';
import IbTypography from '../common/IbTypography';
import IbSwitch from '../common/IbSwitch';
import IbTextArea from '../common/IbTextArea';
import IbButton from '../common/IbButton';
import IbIcon from '../common/IbIcon';
import IbInput from '../common/IbInput';
import IbNumberInput from '../common/IbNumberInput';
import IbScrollSpy from '../common/IbScrollSpy';

import {
  MAIN_CLASS_NAME,
  CONTENT_CLASS_NAME,
  BLOCK_CLASS_NAME,
  BLOCK_ITEM_CLASS_NAME,
  BLOCK_TITLE_CLASS_NAME,
  FOOTER_CLASS_NAME,
  BLOCK_ROW_ITEM_CLASS_NAME,
  BLOCK_ROW_CLASS_NAME,
} from './const';

const CODE_TEXTAREA_ROWS = 3;
const SHOW_CODE_COPIED_TIMEOUT = 2000; //ms

const MIN_BUTTON_SIZE = 2;
const MIN_BUTTON_OFFSET_BOTTOM = 0;
const MIN_BUTTON_OFFSET_RIGHT = 0;

const get_widget_settings_default = () =>
  ({
    enabled: false,
    search_mode_enabled: false,
    welcome_message: {
      enabled: false,
      content: '',
    },
    appearance: {
      title: t('Widget settings: Appearance: Title: Default value'),
      width: '800px',
    },
    toggle_button: {
      size: 40,
      offset: {
        bottom: 32,
        right: 32,
      },
    },
  } as ProjectWidgetOutput);

export interface IProjectWidgetProps {
  projectExternalId?: string;
  settings?: ProjectWidgetOutput;
  onSave: (settings: ProjectWidgetOutput) => Promise<void>;
}

const ProjectWidget: React.FC<IProjectWidgetProps> = ({ projectExternalId, settings, onSave }) => {
  const widgetSettingsDefault = get_widget_settings_default();
  const [widgetSettings, setWidgetSettings] = useState<ProjectWidgetOutput>(settings || widgetSettingsDefault);
  const [saving, setSaving] = useState(false);
  const [showCodeCopied, setShowCodeCopied] = useState(false);
  const [showCodeCopiedTimer, setShowCodeCopiedTimer] = useState<NodeJS.Timeout>();

  const [enabledShowDefault, setEnabledShowDefault] = useState(true);
  const [searchModeEnabledShowDefault, setSearchModeEnabledShowDefault] = useState(true);
  const [welcomeMessageEnabledShowDefault, setWelcomeMessageEnabledShowDefault] = useState(true);
  const [welcomeMessageContentShowDefault, setWelcomeMessageContentShowDefault] = useState(true);
  const [appearanceTitleShowDefault, setAppearanceTitleShowDefault] = useState(true);
  const [appearanceWidthShowDefault, setAppearanceWidthShowDefault] = useState(true);
  const [toggleButtonSizeShowDefault, setToggleButtonSizeShowDefault] = useState(true);
  const [toggleButtonOffsetBottomShowDefault, setToggleButtonOffsetBottomShowDefault] = useState(true);
  const [toggleButtonOffsetRightShowDefault, setToggleButtonOffsetRightShowDefault] = useState(true);

  const scriptCode = `<script async src="${PUBLIC_URL}/widget/init.js" data-project-id="${projectExternalId}" data-api-key="API_KEY"></script>`;

  const enabled = enabledShowDefault
    ? getDefaultIfUndefined(settings?.enabled, widgetSettingsDefault.enabled)
    : widgetSettings.enabled;

  const searchModeEnabled = searchModeEnabledShowDefault
    ? getDefaultIfUndefined(settings?.search_mode_enabled, widgetSettingsDefault.search_mode_enabled)
    : widgetSettings.search_mode_enabled;

  const welcomeMessageEnabled = welcomeMessageEnabledShowDefault
    ? getDefaultIfUndefined(settings?.welcome_message?.enabled, widgetSettingsDefault.welcome_message?.enabled)
    : widgetSettings.welcome_message?.enabled;

  const welcomeMessageContent = welcomeMessageContentShowDefault
    ? getDefaultIfUndefined(settings?.welcome_message?.content, widgetSettingsDefault.welcome_message?.content)
    : widgetSettings.welcome_message?.content;

  const appearanceTitle = appearanceTitleShowDefault
    ? getDefaultIfUndefined(settings?.appearance?.title, widgetSettingsDefault.appearance?.title)
    : widgetSettings.appearance?.title;

  const appearanceWidth = appearanceWidthShowDefault
    ? getDefaultIfUndefined(settings?.appearance?.width, widgetSettingsDefault.appearance?.width)
    : widgetSettings.appearance?.width;

  const toggleButtonSize = toggleButtonSizeShowDefault
    ? getDefaultIfUndefined(settings?.toggle_button?.size, widgetSettingsDefault.toggle_button?.size)
    : widgetSettings.toggle_button?.size;

  const toggleButtonOffsetBottom = toggleButtonOffsetBottomShowDefault
    ? getDefaultIfUndefined(
        settings?.toggle_button?.offset?.bottom,
        widgetSettingsDefault.toggle_button?.offset?.bottom
      )
    : widgetSettings.toggle_button?.offset?.bottom;

  const toggleButtonOffsetRight = toggleButtonOffsetRightShowDefault
    ? getDefaultIfUndefined(settings?.toggle_button?.offset?.right, widgetSettingsDefault.toggle_button?.offset?.right)
    : widgetSettings.toggle_button?.offset?.right;

  const onSaveButtonClick = async () => {
    setSaving(true);
    await onSave(widgetSettings);
    setSaving(false);
  };

  const onEnabledChange = (value: boolean) => {
    setWidgetSettings({ ...widgetSettings, enabled: value } as ProjectWidgetOutput);
    setEnabledShowDefault(false);
  };

  const onSearchModeEnabledChange = (value: boolean) => {
    setWidgetSettings({ ...widgetSettings, search_mode_enabled: value } as ProjectWidgetOutput);
    setSearchModeEnabledShowDefault(false);
  };

  const onWelcomeMessageEnabledChange = (value: boolean) => {
    setWidgetSettings({
      ...widgetSettings,
      welcome_message: { ...widgetSettings.welcome_message, enabled: value },
    } as ProjectWidgetOutput);
    setWelcomeMessageEnabledShowDefault(false);
  };

  const onWelcomeMessageContentChange = (value: string) => {
    setWidgetSettings({
      ...widgetSettings,
      welcome_message: { ...widgetSettings.welcome_message, content: value },
    } as ProjectWidgetOutput);
    setWelcomeMessageContentShowDefault(false);
  };

  const onAppearanceTitleChange = (value: string) => {
    setWidgetSettings({
      ...widgetSettings,
      appearance: { ...widgetSettings.appearance, title: value },
    } as ProjectWidgetOutput);
    setAppearanceTitleShowDefault(false);
  };

  const onAppearanceWidthChange = (value: string) => {
    setWidgetSettings({
      ...widgetSettings,
      appearance: { ...widgetSettings.appearance, width: value },
    } as ProjectWidgetOutput);
    setAppearanceWidthShowDefault(false);
  };

  const onToggleButtonSizeChange = (value: number | string) => {
    setWidgetSettings({
      ...widgetSettings,
      toggle_button: { ...widgetSettings.toggle_button, size: toNumberOrUndefined(value) },
    } as ProjectWidgetOutput);
    setToggleButtonSizeShowDefault(false);
  };

  const onToggleButtonOffsetBottomChange = (value: number | string) => {
    setWidgetSettings({
      ...widgetSettings,
      toggle_button: {
        ...widgetSettings.toggle_button,
        offset: { ...widgetSettings.toggle_button?.offset, bottom: toNumberOrUndefined(value) },
      },
    } as ProjectWidgetOutput);
    setToggleButtonOffsetBottomShowDefault(false);
  };

  const onToggleButtonOffsetRightChange = (value: number | string) => {
    setWidgetSettings({
      ...widgetSettings,
      toggle_button: {
        ...widgetSettings.toggle_button,
        offset: { ...widgetSettings.toggle_button?.offset, right: toNumberOrUndefined(value) },
      },
    } as ProjectWidgetOutput);
    setToggleButtonOffsetRightShowDefault(false);
  };

  const onCopyCodeClick = () => {
    navigator.clipboard.writeText(scriptCode);
    setShowCodeCopied(true);
  };

  const onShowCodeCopiedChange = () => {
    if (showCodeCopied) {
      !!showCodeCopiedTimer && clearTimeout(showCodeCopiedTimer);
      setShowCodeCopiedTimer(setTimeout(() => setShowCodeCopied(false), SHOW_CODE_COPIED_TIMEOUT));
    }
  };
  useEffect(onShowCodeCopiedChange, [showCodeCopied]);

  const renderWidgetBlock = () => (
    <div className={BLOCK_CLASS_NAME}>
      <IbTypography className={BLOCK_TITLE_CLASS_NAME}>
        <h4>{t('Widget settings: Widget')}</h4>
      </IbTypography>
      <div className={BLOCK_ITEM_CLASS_NAME}>
        <IbTypography>
          <IbSwitch checked={enabled} size="small" onClick={onEnabledChange} />
          <IbTypography.Paragraph strong>{t('Widget settings: Widget: Enable')}</IbTypography.Paragraph>
        </IbTypography>
      </div>
      {enabled && (
        <div className={BLOCK_ITEM_CLASS_NAME}>
          <IbTypography.Paragraph strong type="secondary">
            {t('Widget settings: Code')}
          </IbTypography.Paragraph>
          <IbTypography.Paragraph type="secondary">{t('Widget settings: Code: Description')}</IbTypography.Paragraph>
          <IbTextArea rows={CODE_TEXTAREA_ROWS} value={scriptCode} />
          {showCodeCopied ? (
            <IbButton icon={<IbIcon iconName="check" />} status="success" type="link">
              {t('Widget settings: Code: Copied')}
            </IbButton>
          ) : (
            <IbButton icon={<IbIcon iconName="copy-one" />} type="link" onClick={onCopyCodeClick}>
              {t('Widget settings: Code: Copy')}
            </IbButton>
          )}
        </div>
      )}
    </div>
  );

  const renderSearchModeBlock = () => (
    <div className={BLOCK_CLASS_NAME}>
      <IbTypography className={BLOCK_TITLE_CLASS_NAME}>
        <h4>{t('Widget settings: Search')}</h4>
      </IbTypography>
      <div className={BLOCK_ITEM_CLASS_NAME}>
        <IbTypography>
          <IbSwitch checked={searchModeEnabled} size="small" onChange={onSearchModeEnabledChange} />
          <IbTypography.Paragraph strong>{t('Widget settings: Search: Enable')}</IbTypography.Paragraph>
        </IbTypography>
      </div>
    </div>
  );

  const renderWelcomeMessageBlock = () => (
    <div className={BLOCK_CLASS_NAME}>
      <IbTypography className={BLOCK_TITLE_CLASS_NAME}>
        <h4>{t('Widget settings: Welcome message')}</h4>
      </IbTypography>
      <div className={BLOCK_ITEM_CLASS_NAME}>
        <IbTypography>
          <IbSwitch checked={welcomeMessageEnabled} size="small" onChange={onWelcomeMessageEnabledChange} />
          <IbTypography.Paragraph strong>{t('Widget settings: Welcome message: Enable')}</IbTypography.Paragraph>
        </IbTypography>
      </div>
      <div className={BLOCK_ITEM_CLASS_NAME}>
        <IbTypography.Paragraph strong type="secondary">
          {t('Widget settings: Welcome message: Text')}
        </IbTypography.Paragraph>
        <IbTypography.Paragraph type="secondary">
          {t('Widget settings: Welcome message: Description')}
        </IbTypography.Paragraph>
        <IbTextArea
          disabled={!welcomeMessageEnabled}
          rows={5}
          value={welcomeMessageContent}
          onChange={onWelcomeMessageContentChange}
        />
      </div>
    </div>
  );

  const renderAppearanceBlock = () => (
    <div className={BLOCK_CLASS_NAME}>
      <IbTypography className={BLOCK_TITLE_CLASS_NAME}>
        <h4>{t('Widget settings: Appearance')}</h4>
      </IbTypography>
      <div className={BLOCK_ITEM_CLASS_NAME}>
        <IbTypography.Paragraph strong type="secondary">
          {t('Widget settings: Appearance: Title')}
        </IbTypography.Paragraph>
        <IbTypography.Paragraph type="secondary">
          {t('Widget settings: Appearance: Title: Description')}
        </IbTypography.Paragraph>
        <IbInput value={appearanceTitle} onChange={onAppearanceTitleChange} />
      </div>
      <div className={BLOCK_ITEM_CLASS_NAME}>
        <IbTypography.Paragraph strong type="secondary">
          {t('Widget settings: Appearance: Width')}
        </IbTypography.Paragraph>
        <IbTypography.Paragraph type="secondary">
          {t('Widget settings: Appearance: Width: Description')}
        </IbTypography.Paragraph>
        <IbInput value={appearanceWidth} onChange={onAppearanceWidthChange} />
      </div>
    </div>
  );

  const renderToggleButtonBlock = () => (
    <div className={BLOCK_CLASS_NAME}>
      <IbTypography className={BLOCK_TITLE_CLASS_NAME}>
        <h4>{t('Widget settings: Toggle button')}</h4>
      </IbTypography>
      <div className={BLOCK_ITEM_CLASS_NAME}>
        <IbTypography.Paragraph strong type="secondary">
          {t('Widget settings: Toggle button: Size')}
        </IbTypography.Paragraph>
        <IbTypography.Paragraph type="secondary">
          {t('Widget settings: Toggle button: Size: Description')}
        </IbTypography.Paragraph>
        <IbNumberInput
          min={MIN_BUTTON_SIZE}
          value={toggleButtonSize ?? undefined}
          onChange={onToggleButtonSizeChange}
        />
      </div>
      <div className={BLOCK_ITEM_CLASS_NAME}>
        <IbTypography.Paragraph strong>{t('Widget settings: Toggle button: Offset')}</IbTypography.Paragraph>
        <IbTypography.Paragraph type="secondary">
          {t('Widget settings: Toggle button: Offset: Description')}
        </IbTypography.Paragraph>
      </div>
      <div className={BLOCK_ROW_CLASS_NAME}>
        <div className={BLOCK_ROW_ITEM_CLASS_NAME}>
          <IbTypography.Paragraph strong type="secondary">
            {t('Widget settings: Toggle button: Offset: Right')}
          </IbTypography.Paragraph>
          <IbNumberInput
            min={MIN_BUTTON_OFFSET_RIGHT}
            value={toggleButtonOffsetRight ?? undefined}
            onChange={onToggleButtonOffsetRightChange}
          />
        </div>
        <div className={BLOCK_ROW_ITEM_CLASS_NAME}>
          <IbTypography.Paragraph strong type="secondary">
            {t('Widget settings: Toggle button: Offset: Bottom')}
          </IbTypography.Paragraph>
          <IbNumberInput
            min={MIN_BUTTON_OFFSET_BOTTOM}
            value={toggleButtonOffsetBottom ?? undefined}
            onChange={onToggleButtonOffsetBottomChange}
          />
        </div>
      </div>
    </div>
  );

  const mainSection = { id: 'widget', title: t('Widget settings: Widget'), content: renderWidgetBlock() };

  const restSections = [
    { id: 'search-mode', title: t('Widget settings: Search'), content: renderSearchModeBlock() },
    { id: 'welcome-message', title: t('Widget settings: Welcome message'), content: renderWelcomeMessageBlock() },
    { id: 'appearance', title: t('Widget settings: Appearance'), content: renderAppearanceBlock() },
    { id: 'toggle-button', title: t('Widget settings: Toggle button'), content: renderToggleButtonBlock() },
  ];

  return (
    <div className={MAIN_CLASS_NAME}>
      <div className={CONTENT_CLASS_NAME}>
        <IbScrollSpy displayNav sections={!widgetSettings.enabled ? [mainSection] : [mainSection, ...restSections]} />
      </div>
      <div className={FOOTER_CLASS_NAME}>
        <IbButton disabled={saving} onClick={onSaveButtonClick}>
          {t('Widget settings: Save')}
        </IbButton>
      </div>
    </div>
  );
};

export default ProjectWidget;
